import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, BookOpen, Search, RefreshCw } from 'lucide-react';
import { Button } from '../../../components/ui/button.js';
import { Input } from '../../../components/ui/input.js';
import { queryMemories, deleteMemory } from '../../../api/memories.js';
import { MemoryEntry } from '../../../types/legacyControls.js';
import LessonsTab from './LessonsTab.js';
import FixPatternsTab from './FixPatternsTab.js';
import TranscriptsTab from './TranscriptsTab.js';
import PlannedFeaturesTab from './PlannedFeaturesTab.js';
import PlaybooksTab from './PlaybooksTab.js';
import RunbooksTab from './RunbooksTab.js';

interface MemoryItem {
  id: string;
  text: string;
  tags?: string[];
  sessionId?: string;
  metadata?: {
    source?: string;
    createdAt?: string;
    playbookName?: string;
    stepCount?: number;
  };
}

const convertMemoryEntryToMemoryItem = (entry: MemoryEntry): MemoryItem => {
  return {
    id: entry.id,
    text: entry.excerpt || '',
    tags: entry.tags || [],
    metadata: {
      source: entry.type || 'Unknown',
      createdAt: entry.ts ? entry.ts.toISOString() : new Date().toISOString(),
    }
  };
};


interface KnowledgeBasePanelProps {
  isOpen: boolean;
  onClose: () => void;
  fullScreen?: boolean;
}

type KnowledgeBaseTab = 'lessons' | 'fix-patterns' | 'transcripts' | 'planned-features' | 'playbooks' | 'runbooks';

const KnowledgeBasePanel: React.FC<KnowledgeBasePanelProps> = ({ isOpen, onClose, fullScreen = false }) => {
  const [activeTab, setActiveTab] = useState<KnowledgeBaseTab>('lessons');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAgent, setSelectedAgent] = useState('All');
  const [selectedType, setSelectedType] = useState('All');
  const [selectedTime, setSelectedTime] = useState('All');
  const [items, setItems] = useState<MemoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleRefresh = () => {
    console.log('Refreshing knowledge base...');
    fetchDataForActiveTab();
  };

  const fetchDataForActiveTab = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      let query = '';
      
      switch (activeTab) {
        case 'lessons':
          query = 'lesson';
          break;
        case 'fix-patterns':
          query = 'fix pattern';
          break;
        case 'transcripts':
          query = 'transcript';
          break;
        case 'planned-features':
          query = 'planned feature';
          break;
        case 'playbooks':
          query = 'playbook';
          break;
        case 'runbooks':
          query = 'runbook';
          break;
        default:
          query = '';
      }

      const result = await queryMemories(query, undefined, 50);
      const convertedItems = (result || []).map(convertMemoryEntryToMemoryItem);
      setItems(convertedItems);
    } catch (err) {
      console.error('Failed to fetch knowledge base data:', err);
      setError('Unable to load data. Please try again.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    if (isOpen) {
      fetchDataForActiveTab();
    }
  }, [activeTab, isOpen, fetchDataForActiveTab]);

  const handleDeleteItem = async (itemId: string) => {
    try {
      await deleteMemory(itemId);
      setItems(items.filter(item => item.id !== itemId));
    } catch (err) {
      console.error('Failed to delete item:', err);
      setError('Failed to delete item. Please try again.');
    }
  };

  const renderTabContent = () => {
    const commonProps = {
      searchTerm,
      selectedAgent,
      selectedType,
      selectedTime,
      items,
      loading,
      error,
      onDelete: handleDeleteItem,
      onRefresh: fetchDataForActiveTab
    };

    switch (activeTab) {
      case 'lessons':
        return <LessonsTab {...commonProps} />;
      case 'fix-patterns':
        return <FixPatternsTab {...commonProps} />;
      case 'transcripts':
        return <TranscriptsTab {...commonProps} />;
      case 'planned-features':
        return <PlannedFeaturesTab {...commonProps} />;
      case 'playbooks':
        return <PlaybooksTab {...commonProps} />;
      case 'runbooks':
        return <RunbooksTab {...commonProps} />;
      default:
        return <LessonsTab {...commonProps} />;
    }
  };

  if (fullScreen) {
    return (
      <div className="flex flex-col h-full w-full overflow-hidden bg-slate-900">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-700 flex-shrink-0">
          <div>
            <h2 className="text-2xl font-bold text-gradient text-drop-shadow">Beehive Knowledge Base</h2>
            <p className="text-slate-400 mt-1">
              Centralized repository of agent decisions, task resolutions, and build patterns across all Beehive operations.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Filter & Search Section */}
        <div className="p-6 border-b border-slate-700 flex-shrink-0">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
              <Input
                placeholder="Search entries..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 violett-input"
              />
            </div>
            <div className="flex items-center space-x-2">
              <select
                value={selectedAgent}
                onChange={(e) => setSelectedAgent(e.target.value)}
                className="violett-input"
              >
                <option value="All">All Agents</option>
                <option value="BuildStack Agent">BuildStack Agent</option>
                <option value="Code Agent">Code Agent</option>
                <option value="Deploy Agent">Deploy Agent</option>
                <option value="Test Agent">Test Agent</option>
              </select>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="violett-input"
              >
                <option value="All">All Types</option>
                <option value="Bug Fix">Bug Fix</option>
                <option value="Feature">Feature</option>
                <option value="Optimization">Optimization</option>
                <option value="Documentation">Documentation</option>
              </select>
              <select
                value={selectedTime}
                onChange={(e) => setSelectedTime(e.target.value)}
                className="violett-input"
              >
                <option value="All">All Time</option>
                <option value="Last 24h">Last 24h</option>
                <option value="Last Week">Last Week</option>
                <option value="Last Month">Last Month</option>
                <option value="Last 3 Months">Last 3 Months</option>
              </select>
              <Button variant="ghost" size="icon" onClick={handleRefresh}>
                <RefreshCw size={16} />
              </Button>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-slate-800 px-6 flex-shrink-0">
          <button
            className={`px-4 py-3 flex items-center space-x-2 focus:ring-2 focus:ring-blue-400 transition-all duration-200 relative ${
              activeTab === 'lessons' 
                ? 'text-white hover:shadow-lg hover:shadow-blue-500/20' 
                : 'text-slate-400 hover:text-white hover:shadow-lg hover:shadow-blue-500/20'
            }`}
            onClick={() => setActiveTab('lessons')}
          >
            {activeTab === 'lessons' && (
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-slate-300 to-white opacity-80 rounded-b"></div>
            )}
            <BookOpen className="w-4 h-4" />
            <span>Lessons</span>
          </button>
          <button
            className={`px-4 py-3 flex items-center space-x-2 focus:ring-2 focus:ring-blue-400 transition-all duration-200 relative ${
              activeTab === 'fix-patterns' 
                ? 'text-white hover:shadow-lg hover:shadow-blue-500/20' 
                : 'text-slate-400 hover:text-white hover:shadow-lg hover:shadow-blue-500/20'
            }`}
            onClick={() => setActiveTab('fix-patterns')}
          >
            {activeTab === 'fix-patterns' && (
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-slate-300 to-white opacity-80 rounded-b"></div>
            )}
            <span>Fix Patterns</span>
          </button>
          <button
            className={`px-4 py-3 flex items-center space-x-2 focus:ring-2 focus:ring-blue-400 transition-all duration-200 relative ${
              activeTab === 'transcripts' 
                ? 'text-white hover:shadow-lg hover:shadow-blue-500/20' 
                : 'text-slate-400 hover:text-white hover:shadow-lg hover:shadow-blue-500/20'
            }`}
            onClick={() => setActiveTab('transcripts')}
          >
            {activeTab === 'transcripts' && (
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-slate-300 to-white opacity-80 rounded-b"></div>
            )}
            <span>Transcripts</span>
          </button>
          <button
            className={`px-4 py-3 flex items-center space-x-2 focus:ring-2 focus:ring-blue-400 transition-all duration-200 relative ${
              activeTab === 'planned-features' 
                ? 'text-white hover:shadow-lg hover:shadow-blue-500/20' 
                : 'text-slate-400 hover:text-white hover:shadow-lg hover:shadow-blue-500/20'
            }`}
            onClick={() => setActiveTab('planned-features')}
          >
            {activeTab === 'planned-features' && (
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-slate-300 to-white opacity-80 rounded-b"></div>
            )}
            <span>Planned Features</span>
          </button>
          <button
            className={`px-4 py-3 flex items-center space-x-2 focus:ring-2 focus:ring-blue-400 transition-all duration-200 relative ${
              activeTab === 'playbooks' 
                ? 'text-white hover:shadow-lg hover:shadow-blue-500/20' 
                : 'text-slate-400 hover:text-white hover:shadow-lg hover:shadow-blue-500/20'
            }`}
            onClick={() => setActiveTab('playbooks')}
          >
            {activeTab === 'playbooks' && (
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-slate-300 to-white opacity-80 rounded-b"></div>
            )}
            <span>Playbooks</span>
          </button>
          <button
            className={`px-4 py-3 flex items-center space-x-2 focus:ring-2 focus:ring-blue-400 transition-all duration-200 relative ${
              activeTab === 'runbooks' 
                ? 'text-white hover:shadow-lg hover:shadow-blue-500/20' 
                : 'text-slate-400 hover:text-white hover:shadow-lg hover:shadow-blue-500/20'
            }`}
            onClick={() => setActiveTab('runbooks')}
          >
            {activeTab === 'runbooks' && (
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 to-teal-400 opacity-80 rounded-b"></div>
            )}
            <BookOpen className="w-4 h-4" />
            <span>Runbooks</span>
          </button>
        </div>

        {/* Tab Content */}
        <div className="flex-1 p-6 overflow-auto min-h-0">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            className="h-full"
          >
            {renderTabContent()}
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full w-full overflow-hidden">
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
            onClick={onClose}
          />

          <motion.div
            initial={{ opacity: 0, x: 600 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 600 }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 h-full w-[95vw] max-w-[1200px] bg-slate-900 border-l border-slate-700 z-50 flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-slate-700">
              <div>
                <h2 className="text-2xl font-bold text-gradient text-drop-shadow">Beehive Knowledge Base</h2>
                <p className="text-slate-400 mt-1">
                  Centralized repository of agent decisions, task resolutions, and build patterns across all Beehive operations.
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Filter & Search Section */}
            <div className="p-6 border-b border-slate-700">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
                  <Input
                    placeholder="Search entries..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 violett-input"
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <select
                    value={selectedAgent}
                    onChange={(e) => setSelectedAgent(e.target.value)}
                    className="violett-input"
                  >
                    <option value="All">All Agents</option>
                    <option value="BuildStack Agent">BuildStack Agent</option>
                    <option value="Code Agent">Code Agent</option>
                    <option value="Deploy Agent">Deploy Agent</option>
                    <option value="Test Agent">Test Agent</option>
                  </select>
                  <select
                    value={selectedType}
                    onChange={(e) => setSelectedType(e.target.value)}
                    className="violett-input"
                  >
                    <option value="All">All Types</option>
                    <option value="Bug Fix">Bug Fix</option>
                    <option value="Feature">Feature</option>
                    <option value="Optimization">Optimization</option>
                    <option value="Documentation">Documentation</option>
                  </select>
                  <select
                    value={selectedTime}
                    onChange={(e) => setSelectedTime(e.target.value)}
                    className="violett-input"
                  >
                    <option value="All">All Time</option>
                    <option value="Last 24h">Last 24h</option>
                    <option value="Last Week">Last Week</option>
                    <option value="Last Month">Last Month</option>
                    <option value="Last 3 Months">Last 3 Months</option>
                  </select>
                  <Button variant="ghost" size="icon" onClick={handleRefresh}>
                    <RefreshCw size={16} />
                  </Button>
                </div>
              </div>
            </div>

            {/* Tab Navigation */}
            <div className="flex border-b border-slate-800 px-6">
              <button
                className={`px-4 py-3 flex items-center space-x-2 ${
                  activeTab === 'lessons' 
                    ? 'border-b-2 border-brand-primary text-white' 
                    : 'text-slate-400 hover:text-white'
                }`}
                onClick={() => setActiveTab('lessons')}
              >
                <BookOpen className="w-4 h-4" />
                <span>Lessons</span>
              </button>
              <button
                className={`px-4 py-3 flex items-center space-x-2 ${
                  activeTab === 'fix-patterns' 
                    ? 'border-b-2 border-brand-primary text-white' 
                    : 'text-slate-400 hover:text-white'
                }`}
                onClick={() => setActiveTab('fix-patterns')}
              >
                <span>Fix Patterns</span>
              </button>
              <button
                className={`px-4 py-3 flex items-center space-x-2 ${
                  activeTab === 'transcripts' 
                    ? 'border-b-2 border-brand-primary text-white' 
                    : 'text-slate-400 hover:text-white'
                }`}
                onClick={() => setActiveTab('transcripts')}
              >
                <span>Transcripts</span>
              </button>
              <button
                className={`px-4 py-3 flex items-center space-x-2 ${
                  activeTab === 'planned-features' 
                    ? 'border-b-2 border-brand-primary text-white' 
                    : 'text-slate-400 hover:text-white'
                }`}
                onClick={() => setActiveTab('planned-features')}
              >
                <span>Planned Features</span>
              </button>
              <button
                className={`px-4 py-3 flex items-center space-x-2 ${
                  activeTab === 'playbooks' 
                    ? 'border-b-2 border-brand-primary text-white' 
                    : 'text-slate-400 hover:text-white'
                }`}
                onClick={() => setActiveTab('playbooks')}
              >
                <span>Playbooks</span>
              </button>
              <button
                className={`px-4 py-3 flex items-center space-x-2 ${
                  activeTab === 'runbooks' 
                    ? 'border-b-2 border-brand-primary text-white' 
                    : 'text-slate-400 hover:text-white'
                }`}
                onClick={() => setActiveTab('runbooks')}
              >
                <BookOpen className="w-4 h-4" />
                <span>Runbooks</span>
              </button>
            </div>

            {/* Tab Content */}
            <div className="flex-1 p-6 overflow-auto">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
                className="h-full"
              >
                {renderTabContent()}
              </motion.div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
    </div>
  );
};

export default KnowledgeBasePanel;
